import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, List, ListItem, Skeleton, useSnackbar } from '@sauda/ui/kit';
import { Screen } from '../app/chrome.js';
import { deviceName } from '../lib/device-name.js';
import { api } from '../lib/api.js';
import { formatIst } from '../lib/time.js';
import type { SessionInfo } from '../lib/types.js';

/** Signed in devices: this one is marked, the others can be signed out one by one or all at once. */
export function DevicesScreen() {
  const { t } = useTranslation();
  const cache = useQueryClient();
  const snack = useSnackbar();
  const [target, setTarget] = useState<SessionInfo | null>(null);
  const [allOthers, setAllOthers] = useState(false);
  const sessions = useQuery({
    queryKey: ['sessions'],
    queryFn: () => api<SessionInfo[]>('/me/sessions'),
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api(`/me/sessions/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await cache.invalidateQueries({ queryKey: ['sessions'] });
      snack({ text: t('devices.signedOut') });
    },
  });
  const revokeOthers = useMutation({
    mutationFn: () => api('/me/sessions?others=true', { method: 'DELETE' }),
    onSuccess: async () => {
      await cache.invalidateQueries({ queryKey: ['sessions'] });
      snack({ text: t('devices.allSignedOut') });
    },
  });
  const rows = sessions.data ?? [];
  const others = rows.filter((s) => !s.current);
  return (
    <Screen title={t('sessions.title')} large back>
      {sessions.isPending ? <Skeleton height={64} /> : null}
      <List label={t('sessions.title')}>
        {rows.map((s) => (
          <ListItem
            key={s.id}
            title={deviceName(s.device) ?? t('sessions.unknownDevice')}
            supporting={t('devices.lastUsed', { time: formatIst(s.lastUsedAt ?? s.createdAt) })}
            trailing={
              s.current ? (
                <span className="k-t-label">{t('sessions.thisDevice')}</span>
              ) : (
                <Button variant="text" onClick={() => setTarget(s)}>
                  {t('sessions.signOut')}
                </Button>
              )
            }
          />
        ))}
      </List>
      {others.length > 1 ? (
        <Button variant="outlined" onClick={() => setAllOthers(true)}>
          {t('devices.signOutOthers')}
        </Button>
      ) : null}
      <Dialog
        open={target !== null}
        onClose={() => setTarget(null)}
        title={t('devices.confirmTitle')}
        actions={
          <>
            <Button variant="text" onClick={() => setTarget(null)}>
              {t('app.cancel')}
            </Button>
            <Button
              destructive
              onClick={() => {
                if (target !== null) revoke.mutate(target.id);
                setTarget(null);
              }}
            >
              {t('sessions.signOut')}
            </Button>
          </>
        }
      >
        {deviceName(target?.device) ?? t('sessions.unknownDevice')}
      </Dialog>
      <Dialog
        open={allOthers}
        onClose={() => setAllOthers(false)}
        title={t('devices.signOutOthers')}
        actions={
          <>
            <Button variant="text" onClick={() => setAllOthers(false)}>
              {t('app.cancel')}
            </Button>
            <Button
              destructive
              onClick={() => {
                revokeOthers.mutate();
                setAllOthers(false);
              }}
            >
              {t('devices.signOutOthers')}
            </Button>
          </>
        }
      >
        {t('devices.signOutOthersBody')}
      </Dialog>
    </Screen>
  );
}
